import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap, catchError} from 'rxjs/operators';
import { DynamicContentSchemaFactory, MenuItemFacade, PageFacade, SchemaPageFacade, SchemaPageModel, TemplateFacade } from '../../akita';
import { Code, ServiceCallback } from '../../services-utils';
import { PageModel } from '../..';
import { Content } from '../models/content.model';
import { SquidexTool } from '../../../tools';
import { AppToken } from '../../../models/app-token';

const cloneDeep = require('lodash.clonedeep');

@Injectable({ providedIn: 'root' })
export class PageInternalService {

  constructor(
    private pageFacade: PageFacade,
    private dynamicContentSchemaFactory: DynamicContentSchemaFactory,
    private schemaPageFacade: SchemaPageFacade,
    private templateFacade: TemplateFacade,
    private menuItemFacade: MenuItemFacade
    ) { }

  createPage(data: any): Observable<ServiceCallback<Content|any>> {
    return this.templateFacade.createAsPublished({
      body: {iv: "Template of page: " + data.code.iv },
      category: {iv: "default"},
      code: {iv: data.code.iv + "-template"},
      deleted: {iv: false},
      index: {iv: 0}
    }).pipe(mergeMap(template => {
      data.template.iv = [template.id];
      return this.pageFacade.create(data).pipe(mergeMap(page => {
        return this.menuItemFacade.createAsPublished({
          code: { iv: page.data.code.iv },
          page: { iv : page.id },
          index: {iv:0},
          deleted: {iv:false}
        }).pipe(map(() => {
          return new ServiceCallback<PageModel>(Code.Success, "Page created", page);
        }), catchError((error: any) => {
          this.pageFacade.removeHard(page.id).subscribe();
          this.templateFacade.removeHard(template.id).subscribe();
          return of(new ServiceCallback<any>(Code.ServeurError, "MenuItem creation failed in internal page service create function", error));
        }));
      }), catchError((error: any) => {
        this.templateFacade.removeHard(template.id).subscribe();
        return of(new ServiceCallback<any>(Code.ServeurError, "Page creation failed in internal page service create function", error));
      }));
    }), catchError((error: any) => {
      return of(new ServiceCallback<any>(Code.ServeurError, "Template creation failed in internal page service create function", error));
    }));
  }

  deletePage(pageId: string): Observable<ServiceCallback> {
    return of(new ServiceCallback(Code.NotImplemented));
  }

  private clonePageContent(contentId: string, name:string, url: {[key: string]: string })
  : Observable<ServiceCallback<Content|any>> {

    console.log(contentId);
    return this.schemaPageFacade.getSchemaPageByContentId(contentId, true).pipe(mergeMap(schemaPage => {
      console.log(schemaPage);
      const schemaName= schemaPage.data.contenttype.iv;

      console.log(schemaName);
      const contentFacade = this.dynamicContentSchemaFactory.getDynamicContentSchemaFacade(schemaName);

      return contentFacade.get(contentId, true).pipe(mergeMap(content => {
        const clonedContentData = cloneDeep(content.data);
        let squidexTool = new SquidexTool();
        let sanitizedContentCode = squidexTool.sanitizeForUniqueName(name);

        clonedContentData.pageName = { iv: name };
        clonedContentData.code = {iv: sanitizedContentCode};
        clonedContentData.url = url;
        return contentFacade.createAsPublished(clonedContentData).pipe(mergeMap(newContent => {

          let clonedSchemaPageData = cloneDeep(schemaPage.data);
          clonedSchemaPageData.code = {iv: sanitizedContentCode};
          clonedSchemaPageData.contentid.iv = newContent.id;

          return this.schemaPageFacade.createAsPublished(clonedSchemaPageData).pipe(mergeMap(newSchemaPage => {

            return this.menuItemFacade.getMenuItemsByPageId(contentId).pipe(mergeMap(menuItems => {
              let clonedMenuItemData = cloneDeep(menuItems[0].data);

              clonedMenuItemData.page.iv = newSchemaPage.id;
              clonedMenuItemData.code = {iv: `${schemaName}/${sanitizedContentCode}`};

              return this.menuItemFacade.createAsPublished(clonedMenuItemData).pipe(map(() => {
                return new ServiceCallback<Content>(Code.Success,
                "Page cloned",
                newContent);
              }), catchError((error: any) => {
              this.schemaPageFacade.removeHard(newSchemaPage.id).subscribe();
              contentFacade.removeHard(newContent.id).subscribe();
              return of(new ServiceCallback<any>(Code.ServeurError,
                "MenuItem creation failed in internal page service clone function",
                error));
            }));
          }), catchError((error: any) => {
            this.schemaPageFacade.removeHard(newSchemaPage.id).subscribe();
            contentFacade.removeHard(newContent.id).subscribe();
            return of(new ServiceCallback<any>(Code.ServeurError,
              "Cannot get menu item in internal page service clone function",
              error));
          }));
          }), catchError((error: any) => {
            contentFacade.removeHard(newContent.id).subscribe();
            return of(new ServiceCallback<any>(Code.ServeurError, "SchemaPage creation failed in internal page service clone function", error));
          }));
        }), catchError((error: any) => {
          console.log("error :");
        console.log(error);
          return of(new ServiceCallback<any>(Code.ClientError, "Content creation failed in internal page service clone function", error));
        }));
      }), catchError((error: any) => {
        console.log("error :");
        console.log(error);
        return of(new ServiceCallback<any>(Code.ClientError, "Cannot get content in internal page service clone function", error));
      }));
    }), catchError((error: any) => {
      return of(new ServiceCallback<any>(Code.ClientError, "Cannot get schema-page in internal page service clone function", error));
    }));
  }

  private cloneSinglePage(pageId: string, name:string, url: {[key: string]: string })
  : Observable<ServiceCallback<Content|any>> {
    return this.pageFacade.get(pageId, true).pipe(mergeMap(originalPage => {
      if(
        !originalPage.data.template ||
        !originalPage.data.template.iv ||
        originalPage.data.template.iv.length == 0 ||
        !originalPage.data.template.iv[0]
      ) {
        return of(new ServiceCallback(Code.ClientError, "No template id found for page in internal page service clone function"));
      }
      let data = cloneDeep(originalPage.data);
      let squidexTool = new SquidexTool();
      data.code = {iv: squidexTool.sanitizeForUniqueName(name)};
      data.pageName = {iv: name};
      data.url = url;

      return this.templateFacade.get(originalPage.data.template.iv[0], true).pipe(mergeMap(originalTemplate => {
        let templateData = cloneDeep(originalTemplate.data);
        templateData.code = {iv: data.code.iv + "-template"};
        templateData.body = {iv: originalTemplate.data.body.iv + " - Cloned from " + originalPage.data.code.iv};

        return this.templateFacade.createAsPublished(templateData).pipe(mergeMap(template => {
          data.template.iv = [template.id];

          return this.pageFacade.create(data).pipe(mergeMap(page => {
            return this.menuItemFacade.createAsPublished({
              code: { iv: page.data.code.iv },
              page: { iv : page.id },
              index: {iv:0},
              deleted: {iv:false}
            }).pipe(map(() => {
              return new ServiceCallback<PageModel>(Code.Success, "Page cloned", page);
            }), catchError((error: any) => {
              this.pageFacade.removeHard(page.id).subscribe();
              this.templateFacade.removeHard(template.id).subscribe();
              return of(new ServiceCallback<any>(Code.ServeurError,
                "MenuItem creation failed in internal page service clone function",
                error));
            }));
          }), catchError((error: any) => {
            this.templateFacade.removeHard(template.id).subscribe();
            return of(new ServiceCallback<any>(Code.ServeurError,
              "Page creation failed in internal page service clone function",
              error));
          }));
        }), catchError((error: any) => {
          return of(new ServiceCallback<any>(Code.ServeurError,
            "Template creation failed in internal page service clone function",
            error));
        }));
      }) ) ;
    }));
  }

  clonePage(pageId: string, name:string, url: {[key: string]: string }, isContent: boolean = false)
  : Observable<ServiceCallback<Content|any>> {
    return isContent ? this.clonePageContent(pageId, name, url) : this.cloneSinglePage(pageId, name, url);
  }
}
